/*
**  rui-tabset.js
**
**  rui-tabset component class
*/
import { LitElement, html, css } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
export class RuiTabset extends LitElement {
    static get is() {
        return 'rui-tabset';
    }
    static get properties() {
        return {
            selectedTabId: { type: String },
            isFullHeight: { type: Object, reflect: true }
        };
    }
    constructor() {
        super();
        this._selectedTabId = '';
        this.isFullHeight = false;
        this._tabList = [];
        this._hasTabHeaderExtra = false;
        this._isOverflowed = false;
        this._tabScrollPixels = 250;
        this._scrollLeftEnabled = false;
        this._scrollRightEnabled = false;
    }
    // Custom setter for selectedTabId property
    set selectedTabId(newValue) {
        if (this._selectedTabId !== newValue) {
            // Store new value
            this._selectedTabId = newValue;
            this.requestUpdate();
            // Fire value change event
            Promise.resolve().then(() => {
                let valueChangeEvent = new CustomEvent('rui-selected-tab-change', {
                    detail: this._selectedTabId,
                    bubbles: true,
                    composed: true
                });
                this.dispatchEvent(valueChangeEvent);
            });
            // Is the selected tab already in this tab list?
            let found = false;
            let foundIndex = 0;
            while (foundIndex < this._tabList.length && !found) {
                if (this._tabList[foundIndex].id == this._selectedTabId) {
                    found = true;
                }
                else {
                    foundIndex++;
                }
            }
            // Loop through tabs, then set active one based on selectedTabId
            let tabItems = this.querySelectorAll('rui-tab');
            if (tabItems.length) {
                tabItems.forEach(tabItem => {
                    if (tabItem._id == this._selectedTabId) {
                        tabItem._isActive = found;
                    }
                    else {
                        tabItem._isActive = !found;
                    }
                });
            }
            // Make sure tab label of selected tab is in view for the user
            this.scrollTabLabelIntoViewHorizontally();
        }
    }
    // Custom getter for selectedTabId property
    get selectedTabId() {
        return this._selectedTabId;
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('rui-tab-updated', (e) => this.tabUpdated(e));
        this.addEventListener('rui-tab-id-updated', (e) => this.tabIdUpdated(e));
        window.addEventListener('rui-nav-display-state', (e) => this.navWidthChange(e));
        window.addEventListener('resize', (e) => this.windowResize(e));
        window.setTimeout(() => {
            // Test for initial tab overflow
            this.testTabOverflow();
            // Test for initial tab scrolling
            this.testScrollBounds();
            // Auto-select the first tab
            if (this._tabList.length > 0 && this.selectedTabId === '') {
                this.selectedTabId = this._tabList[0].id;
            }
        }, 100);
    }
    disconnectedCallback() {
        this.removeEventListener('rui-tab-updated', (e) => this.tabUpdated(e));
        this.removeEventListener('rui-tab-id-updated', (e) => this.tabIdUpdated(e));
        window.removeEventListener('rui-nav-display-state', (e) => this.navWidthChange(e));
        window.removeEventListener('resize', (e) => this.windowResize(e));
        super.disconnectedCallback();
    }
    tabSlotChange() {
        let tabItems = this.querySelectorAll('rui-tab');
        if (tabItems) {
            this._tabList = [];
            tabItems.forEach(tabItem => {
                tabItem.fireTabUpdated();
            });
        }
        window.setTimeout(() => {
            this.testTabOverflow();
            this.testScrollBounds();
        }, 50);
        this.requestUpdate();
    }
    tabUpdated(e) {
        let matchingTabs = this._tabList.filter(tabInfo => tabInfo.id == e.detail.id);
        if (matchingTabs.length < 1) {
            this._tabList.push(e.detail);
        }
        else {
            matchingTabs[0].labelText = e.detail.labelText;
            matchingTabs[0].labelTooltip = e.detail.labelTooltip;
            matchingTabs[0].badge = e.detail.badge;
            matchingTabs[0].isDisabled = e.detail.isDisabled;
            matchingTabs[0].tabLabelExtra = e.detail.tabLabelExtra;
        }
        this.requestUpdate();
    }
    tabIdUpdated(e) {
        let matchingTabs = this._tabList.filter(tabInfo => tabInfo.id == e.detail.prevId);
        if (matchingTabs.length) {
            matchingTabs[0].id = e.detail.newId;
            this.requestUpdate();
        }
    }
    windowResize() {
        this.testTabOverflow();
        this.testScrollBounds();
    }
    navWidthChange() {
        window.setTimeout(() => {
            this.testTabOverflow();
            this.testScrollBounds();
        }, 100);
    }
    testTabOverflow() {
        const el = this.shadowRoot.querySelector('.rui-tabset-container');
        if (el) {
            const isOverflowed = el.scrollWidth > el.clientWidth;
            if (isOverflowed != this._isOverflowed) {
                this._isOverflowed = isOverflowed;
                this.scrollTabLabelIntoViewHorizontally();
                this.requestUpdate();
            }
        }
    }
    scrollTabLabelIntoViewHorizontally() {
        const selectedTabLabel = this.shadowRoot.querySelector('#rui-tab-label-' + this._selectedTabId);
        const tabLabelContainer = this.shadowRoot.querySelectorAll('.rui-tabset-container');
        // if tabset is overflow and we have a selected tab and tab container, see if it's hidden and show it
        if (this._isOverflowed && selectedTabLabel && tabLabelContainer) {
            const tabLabelOffset = selectedTabLabel.offsetLeft + selectedTabLabel.offsetWidth;
            const tabLabelContainerOffset = tabLabelContainer[0].scrollLeft + tabLabelContainer[0].offsetWidth;
            // if tab label is hidden to left
            if (tabLabelContainer[0].scrollLeft > selectedTabLabel.offsetLeft) {
                tabLabelContainer[0].scrollLeft = selectedTabLabel.offsetLeft;
            }
            // if tab label is hidden to right
            if (tabLabelContainerOffset < tabLabelOffset) {
                tabLabelContainer[0].scrollLeft += tabLabelOffset - tabLabelContainerOffset;
            }
        }
    }
    ;
    tabClick(e) {
        e.preventDefault();
        e.stopPropagation();
        const clickTree = (e.composedPath() || []).filter(item => item.localName === 'button');
        if (clickTree.length > 0) {
            let tabId = clickTree[0].getAttribute('data-rui-tab-id');
            this.selectedTabId = tabId;
        }
    }
    showTabByPosition(position) {
        if (position >= 0 && position < this._tabList.length) {
            this.selectedTabId = this._tabList[position].id;
        }
    }
    showTabByLabel(labelText) {
        let matchingTabs = this._tabList.filter(tabInfo => tabInfo.labelText == labelText);
        if (matchingTabs.length > 0) {
            this.selectedTabId = matchingTabs[0].id;
        }
    }
    showTabById(id) {
        let matchingTabs = this._tabList.filter(tabInfo => tabInfo.id == id);
        if (matchingTabs.length > 0) {
            this.selectedTabId = matchingTabs[0].id;
        }
    }
    scrollTabsLeft(e) {
        e.preventDefault();
        e.stopPropagation();
        const el = this.shadowRoot.querySelector('.rui-tabset-container');
        if (el) {
            el.scrollBy(-this._tabScrollPixels, 0);
        }
    }
    scrollTabsRight(e) {
        e.preventDefault();
        e.stopPropagation();
        const el = this.shadowRoot.querySelector('.rui-tabset-container');
        if (el) {
            el.scrollBy(this._tabScrollPixels, 0);
        }
    }
    testScrollBounds() {
        const el = this.shadowRoot.querySelector('.rui-tabset-container');
        if (el) {
            this._scrollLeftEnabled = el.scrollLeft > 0;
            this._scrollRightEnabled = (el.scrollLeft + el.offsetWidth) < el.scrollWidth;
            this.requestUpdate();
        }
    }
    tabHeaderScrolled() {
        this.testScrollBounds();
    }
    render() {
        const outerClasses = {
            ['rui-tabset-outer-container']: true,
            ['rui-tabset-outer-container-full-height']: this.isFullHeight
        };
        const leftScrollBtnClasses = {
            ['rui-tabset-overflow-btn']: true,
            ['rui-tabset-overflow-btn-left']: true,
            ['rui-tabset-overflow-btn-disabled']: !this._scrollLeftEnabled
        };
        const rightScrollBtnClasses = {
            ['rui-tabset-overflow-btn']: true,
            ['rui-tabset-overflow-btn-right']: true,
            ['rui-tabset-overflow-btn-disabled']: !this._scrollRightEnabled
        };
        if (this.querySelector(`*[slot='tab-header-extra']`)) {
            this._hasTabHeaderExtra = true;
        }
        return html `
            <div class="${classMap(outerClasses)}">
                <div class="rui-tabset-overflow-container">
                    ${this._isOverflowed ? html `
                        <button class="${classMap(leftScrollBtnClasses)}" @click="${this.scrollTabsLeft}" ?disabled="${!this._scrollLeftEnabled}">
                            <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 16 16">
                                <defs>
                                    <clipPath id="clip-path">
                                    <rect width="16" height="16" transform="translate(121 1034)" fill="#fff" stroke="#707070" stroke-width="1"/>
                                    </clipPath>
                                </defs>
                                <g transform="translate(1050 -121) rotate(90)" clip-path="url(#clip-path)">
                                    <path d="M897.088,593.65l-7.565,8.148a.44.44,0,0,1-.642,0l-7.564-8.148a.425.425,0,0,1,.321-.713h15.129A.425.425,0,0,1,897.088,593.65Z" transform="translate(-760.202 444.563)" fill="#040405"/>
                                </g>
                            </svg>
                        </button>
                    ` : ''}
                    <div style="flex:1 0 0%" class="rui-tabset-container" role="tablist" @scroll="${this.tabHeaderScrolled}">
                        ${this._tabList.map((tabInfo, index) => html `
                            <button id="rui-tab-label-${tabInfo.id}"
                                class="rui-tab-header ${tabInfo.id == this._selectedTabId ? 'rui-tab-header-active' : ''}" title="${tabInfo.labelTooltip}"
                                data-rui-tab-id="${tabInfo.id}" ?disabled="${tabInfo.isDisabled}" @click="${this.tabClick}"
                                role="tab" aria-selected="${tabInfo.id == this.selectedTabId}" aria-disabled="${tabInfo.isDisabled}"
                                aria-controls="rui-tab-content-${tabInfo.id}" aria-setsize="${this._tabList.length}" aria-posinset="${index + 1}">
                                ${tabInfo.labelText}
                                ${tabInfo.badge ? html `<span class="rui-tab-header-badge ${tabInfo.isDisabled ? 'rui-tab-header-badge-disabled' : ''}">${tabInfo.badge}</span>` : ''}
                                ${tabInfo.tabLabelExtra ? html `<div class="rui-tab-label-extra"><div class="rui-tab-label-extra-content">${tabInfo.tabLabelExtra}</div></div>` : ''}
                            </button>
                        `)}
                        ${this._hasTabHeaderExtra ? html `<div class="rui-tab-header-extra"><slot name="tab-header-extra" class="rui-tab-header-extra-content"></slot></div>` : ''}
                    </div>
                    ${this._isOverflowed ? html `
                        <button class="${classMap(rightScrollBtnClasses)}" @click="${this.scrollTabsRight}" ?disabled="${!this._scrollRightEnabled}">
                            <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 16 16">
                                <defs>
                                <clipPath id="clip-path">
                                    <rect width="16" height="16" transform="translate(121 1034)" fill="#fff" stroke="#707070" stroke-width="1"/>
                                </clipPath>
                                </defs>
                                <g transform="translate(-1034 137) rotate(-90)" clip-path="url(#clip-path)">
                                <path d="M897.088,593.65l-7.565,8.148a.44.44,0,0,1-.642,0l-7.564-8.148a.425.425,0,0,1,.321-.713h15.129A.425.425,0,0,1,897.088,593.65Z" transform="translate(-760.202 444.563)" fill="#040405"/>
                                </g>
                            </svg>
                        </button>
                    ` : ''}
                    </div>
                <div class="rui-tab-content">
                    <slot @slotchange="${this.tabSlotChange}"></slot>
                </div>
            </div>
        `;
    }
    static get styles() {
        return css `
            :host {
                display: block;
            }
                        
            :host([isFullHeight="true"]) {
                height: 100%;
            }

            .rui-tabset-outer-container {
                display: block;
            }

            .rui-tabset-outer-container {
                display: block;
            }

            .rui-tabset-outer-container-full-height {
                display: flex;
                flex-direction: column;
                height: 100%;
                overflow-y: hidden;
            }
            
            .rui-tabset-container {
                height: 46px;
                overflow: visible hidden;
                position: relative;
                scroll-behavior: smooth;
                white-space: nowrap;
                width: 100%;
                -ms-overflow-style: none; /* for Internet Explorer, Edge */
                scrollbar-width: none; /* for Firefox */
            }

            .rui-tabset-outer-container-full-height .rui-tabset-container {
                flex: 0 0 auto;
            }
            
            .rui-tabset-container::-webkit-scrollbar {
                display: none; /* for Chrome, Safari, and Opera */
            }
            
            .rui-tab-header {
                background-color: transparent;
                box-sizing: border-box;
                border: none;
                border-bottom: 2px solid transparent;
                border-radius: 8px 8px 0 0;
                color: var(--rui-text-regular, #09175b);
                cursor: pointer;
                display: inline-block;
                font-family: var(--rui-font-family-chrome, Gellix, "Noto Sans", Roboto, Arial, -apple-system, system-ui, sans-serif);
                font-size: var(--rui-btn-font-size-md, 13px);
                font-weight: 600;
                height: 100%;
                line-height: var(--rui-btn-line-height-md, 1.42857143);
                margin: 0;
                padding: 2px 18px;
                transition: background 0.25s;
                vertical-align: top;
                -webkit-font-smoothing: antialiased;
            }

            .rui-tab-label-extra {
                display: inline-block;
                margin-left: 4px;
            }

            .rui-tab-label-extra-content {
                display: flex;
                height: 100%;
                align-items: center;
            }
            
            .rui-tab-header-extra {
                box-sizing: border-box;
                border: none;
                border-bottom: 2px solid transparent;
                display: inline-block;
                height: 100%;
                min-height: 46px;
                padding: 0 0 0 10px;
                vertical-align: top;
            }

            .rui-tab-header-extra-content {
                display: flex;
                height: 100%;
                align-items: center;
            }

            @media (prefers-reduced-motion: reduce) {
                .rui-tab-header {
                    transition: none;
                }
            }

            .rui-tabset-overflow-container {
                display: flex;
            }

            .rui-tabset-overflow-btn {
                align-self: center;
                background: transparent;
                border: 1px solid var(--rui-line);
                border-radius: 12px;
                flex: 0 0 auto;
                height: 24px;
                width: 24px;
            }

            .rui-tabset-overflow-btn-disabled {
                background: var(--rui-btn-secondary-bg-disabled);
                border-color: var(--rui-btn-secondary-border-disabled);
            }

            .rui-tabset-overflow-btn-left {
                margin-right: 5px;
            }

            .rui-tabset-overflow-btn-right {
                margin-left: 5px;
            }

            .rui-tabset-overflow-btn svg {
                height: 8px;
                width: 8px;
            }

            .rui-tabset-overflow-btn svg path {
                fill: var(--rui-text-regular);
            }

            .rui-tabset-overflow-btn-disabled svg path {
                fill: var(--rui-text-disabled);
            }

            .rui-tab-header-active {
                border-bottom-color: var(--rui-text-bright, #203660);
                color: var(--rui-text-bright, #203660);
            }
            
            .rui-tab-header:not(:disabled):hover {
                background-color: var(--rui-list-item-hovered-bg, #e5e8eb);
            }
            
            .rui-tab-header:focus {
                background-color: var(--rui-list-item-hovered-bg, #e5e8eb);
                outline: var(--rui-outline-width, 1px) var(--rui-outline-style, solid) var(--rui-btn-primary-border, #085ce5);
                outline-offset: -1px;
            }
            
            .rui-tab-header:disabled {
                color: var(--rui-text-disabled, #999);
                cursor: not-allowed;
            }
            
            .rui-tab-header-badge {
                background-color: var(--rui-content-primary-bg, #fff);
                border-color: var(--rui-text-regular, #09175b);
                border-radius: var(--rui-btn-badge-border-radius, 10px);
                border-style: var(--rui-btn-badge-border-style, solid);
                border-width: var(--rui-btn-badge-border-width, 1px);
                color: var(--rui-text-regular, #09175b);
                display: inline-block;
                font-size: var(--rui-btn-badge-font-size, 12px);
                font-weight: var(--rui-btn-badge-font-weight, 700);
                line-height: var(--rui-btn-badge-line-height, 1);
                margin: -2px 0 0 4px;
                min-width: var(--rui-btn-badge-min-width, 10px);
                opacity: 0.66;
                padding: var(--rui-btn-badge-padding, 2px 6px 4px 6px);
                text-align: center;
                vertical-align: middle;
                white-space: nowrap;
            }
            
            .rui-tab-header-active .rui-tab-header-badge {
                border-color: var(--rui-text-bright, #203660);
                color: var(--rui-text-bright, #203660);
                opacity: 0.9;
            }
            
            .rui-tab-header-badge-disabled {
                color: var(--rui-btn-primary-fg-disabled, #999);
                background-color: var(--rui-btn-primary-bg-disabled, #e9e9e9);
                border-color: var(--rui-btn-primary-border-disabled, #cdcdcd);
            }
            
            .rui-tab-content {
                border: none;
                border-top: 1px solid var(--rui-line, #e0e8f5);
                display: block;
                padding-top: 15px;
            }

            .rui-tabset-outer-container-full-height .rui-tab-content {
                flex: 1 0 0%;
                overflow-y: auto;
            }

            @media print {
                .rui-tab-header, .rui-tab-header-extra {
                    background-color: transparent;
                    color: #000000;
                }    
            
                .rui-tab-header-active {
                    border-bottom-color: #000000;
                }
            }
        `;
    }
}
customElements.get(RuiTabset.is) || customElements.define(RuiTabset.is, RuiTabset);
